import type { Question } from '../data/verticals';
import Field from './Field';
import Select from './Select';
import NumberInput from './NumberInput';
import MultiSelect from './MultiSelect';
import BooleanField from './BooleanField';

interface QuestionFieldProps {
  question: Question;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
}

/**
 * Renders one Question from verticals.ts with the right control. The customer
 * never sees a question id or a tag name — they are answering questions about
 * their building, not filling in a schema.
 */
export default function QuestionField({ question: q, value, onChange, error }: QuestionFieldProps) {
  switch (q.type) {
    case 'number':
      return (
        <NumberInput
          id={q.id}
          label={q.label}
          hint={q.help}
          error={error}
          value={typeof value === 'number' ? value : ''}
          onValueChange={(v) => onChange(v === '' ? null : v)}
          min={q.min}
          max={q.max}
          step={q.step ?? 1}
          suffix={q.unit}
        />
      );

    case 'select':
      return (
        <Select
          id={q.id}
          label={q.label}
          hint={q.help}
          error={error}
          placeholder="Choose one"
          options={(q.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );

    case 'multi':
      return (
        <MultiSelect
          legend={q.label}
          hint={q.help}
          error={error}
          options={(q.options ?? []).map((o) => ({ value: o.value, label: o.label }))}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={(next) => onChange(next)}
          max={q.max}
        />
      );

    case 'boolean':
      return (
        <BooleanField
          id={q.id}
          label={q.label}
          hint={q.help}
          value={typeof value === 'boolean' ? value : undefined}
          onChange={onChange}
        />
      );

    case 'range':
    case 'text':
    default:
      return (
        <Field
          id={q.id}
          label={q.label}
          hint={q.help}
          error={error}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );
  }
}

/** A question is only asked when its showIf condition is met. */
export function isVisible(q: Question, answers: Record<string, unknown>): boolean {
  if (!q.showIf) return true;
  return answers[q.showIf.questionId] === q.showIf.equals;
}

/** Required questions must be answered before the customer can move on. */
export function isAnswered(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}
